import { z } from 'zod';
import { ItemIdSchema } from './items';

/*
 * What a character knows of the map's wealth (RES-DISTRIBUTION D3, technical 5-7): what it
 * surveyed or read on a survey map, never the wealth itself (OC-2). Every grade travels as an
 * interval that holds the real one; the real grade, the exact reserve and the server's seed never
 * leave the server.
 */

/** What is left of a deposit when it was surveyed (SD-2): untouched, worked, almost spent. */
export const RESERVE_STATES = ['intact', 'worked', 'spent'] as const;
export const ReserveStateSchema = z.enum(RESERVE_STATES);
export type ReserveState = z.infer<typeof ReserveStateSchema>;

/** Where a piece of knowledge came from: the character's own survey, or a map it read. */
export const KnowledgeOriginSchema = z.enum(['own', 'map']);
export type KnowledgeOrigin = z.infer<typeof KnowledgeOriginSchema>;

/** The continuous fields a survey reads (RD-1, SD-3). */
export const SURVEY_FIELDS = ['wood', 'fertility'] as const;
export const SurveyFieldSchema = z.enum(SURVEY_FIELDS);
export type SurveyField = z.infer<typeof SurveyFieldSchema>;

/**
 * A deposit known (SD-1): its material and its shape (centre and radius, world metres), its grade
 * within `[low, high]` (0 to 1; the real one lies inside), the margin it was known with, and its
 * reserve as it stood `at` (game time, SD-2).
 */
export const KnownDepositSchema = z.object({
  id: z.string().min(1).max(64),
  material: ItemIdSchema,
  x: z.number(),
  y: z.number(),
  radius: z.number().nonnegative(),
  low: z.number().min(0).max(1),
  high: z.number().min(0).max(1),
  margin: z.number().min(0).max(1),
  reserve: ReserveStateSchema,
  at: z.number().int().nonnegative(),
  origin: KnowledgeOriginSchema,
  /**
   * A Depth's deep vein (DEPTH-ECOLOGY DP-4, GK-8): worked by hand (`vein`, its `id` its opaque
   * mark), never by a mine. A spent one leaves every knower's knowledge.
   */
  deep: z.literal(true).optional(),
});
export type KnownDeposit = z.infer<typeof KnownDepositSchema>;

/**
 * A field's cell known (SD-3): cell (`cx`, `cy`) of the survey grid (`gridM` metres, sent in the
 * initial state), its band (0 poor, 1 middling, 2 rich) and the margin it was read with.
 */
export const KnownFieldSchema = z.object({
  field: SurveyFieldSchema,
  cx: z.number().int(),
  cy: z.number().int(),
  band: z.number().int().min(0).max(2),
  margin: z.number().min(0).max(1),
  at: z.number().int().nonnegative(),
  origin: KnowledgeOriginSchema,
});
export type KnownField = z.infer<typeof KnownFieldSchema>;

/** A circle surveyed (world metres): what lies in it is known, nothing found included. */
export const SoundingSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  radius: z.number().int().positive(),
  at: z.number().int().nonnegative(),
  origin: KnowledgeOriginSchema,
});
export type Sounding = z.infer<typeof SoundingSchema>;

/** Everything a character knows of the wealth, or a part of it (an area, a map's copy). */
export const SurveyKnowledgeSchema = z.object({
  deposits: z.array(KnownDepositSchema),
  fields: z.array(KnownFieldSchema),
  soundings: z.array(SoundingSchema),
});
export type SurveyKnowledge = z.infer<typeof SurveyKnowledgeSchema>;
